import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../api';

/**
 * Globaler Activity Tracker der ALLE User-Interaktionen automatisch loggt
 */
export default function GlobalActivityTracker() {
  const location = useLocation();

  // Log page visits
  useEffect(() => {
    // Don't log if user is not authenticated
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Don't log login/logout pages
    if (location.pathname === '/login' || location.pathname === '/logout') return;
    
    // Add small delay to ensure auth is fully established after login
    const timer = setTimeout(() => {
      const pageName = getPageName(location.pathname);
      logActivity({
        action_type: 'page.visit',
        action_category: 'user_clicks',
        description: `Seite aufgerufen: ${pageName}`,
        entity_type: 'page',
        metadata: {
          path: location.pathname,
          search: location.search,
          hash: location.hash
        }
      }).catch(() => {});
    }, 500); // 500ms delay
    
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  // Global click tracker
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Don't log if user is not authenticated
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Don't log clicks on login/logout pages
      if (window.location.pathname === '/login' || window.location.pathname === '/logout') return;
      
      const target = e.target as HTMLElement;
      
      // Track button clicks
      if (target.tagName === 'BUTTON' || target.closest('button')) {
        const button = target.tagName === 'BUTTON' ? target : target.closest('button');
        const buttonText = button?.textContent?.trim() || 'Unbekannter Button';
        const buttonType = button?.getAttribute('type') || 'button';
        const buttonClasses = button?.className || '';
        
        logActivity({
          action_type: 'button.click',
          action_category: 'user_clicks',
          description: `Button geklickt: ${buttonText}`,
          entity_type: 'button',
          metadata: {
            button_text: buttonText,
            button_type: buttonType,
            button_classes: buttonClasses,
            page: location.pathname
          }
        }).catch(() => {});
      }
      
      // Track link clicks
      else if (target.tagName === 'A' || target.closest('a')) {
        const link = target.tagName === 'A' ? target as HTMLAnchorElement : target.closest('a');
        const linkText = link?.textContent?.trim() || 'Unbekannter Link';
        const href = link?.getAttribute('href') || '';
        
        logActivity({
          action_type: 'link.click',
          action_category: 'user_clicks',
          description: `Link geklickt: ${linkText}`,
          entity_type: 'link',
          metadata: {
            link_text: linkText,
            href: href,
            page: location.pathname
          }
        }).catch(() => {});
      }
      
      // Track input clicks (focus on form fields)
      else if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        const inputElement = target as HTMLInputElement;
        const inputName = inputElement.name || inputElement.id || inputElement.placeholder || 'Unbekanntes Feld';
        const inputType = inputElement.type || inputElement.tagName.toLowerCase();
        
        logActivity({
          action_type: 'input.focus',
          action_category: 'user_clicks',
          description: `Eingabefeld fokussiert: ${inputName}`,
          entity_type: 'input',
          metadata: {
            input_name: inputName,
            input_type: inputType,
            page: location.pathname
          }
        }).catch(() => {});
      }
      
      // Track clicks on elements with onclick handlers
      else if (target.onclick || target.getAttribute('onclick')) {
        const elementText = target.textContent?.trim()?.substring(0, 50) || 'Interaktives Element';
        const elementTag = target.tagName.toLowerCase();
        
        logActivity({
          action_type: 'element.click',
          action_category: 'user_clicks',
          description: `Element geklickt: ${elementText}`,
          entity_type: 'interactive_element',
          metadata: {
            element_text: elementText,
            element_tag: elementTag,
            page: location.pathname
          }
        }).catch(() => {});
      }
    };

    // Global form submission tracker
    const handleSubmit = (e: SubmitEvent) => {
      const form = e.target as HTMLFormElement;
      const formName = form.name || form.id || 'Unbekanntes Formular';
      const formAction = form.action || 'Keine Action';
      
      logActivity({
        action_type: 'form.submit',
        action_category: 'user_clicks',
        description: `Formular abgesendet: ${formName}`,
        entity_type: 'form',
        metadata: {
          form_name: formName,
          form_action: formAction,
          page: location.pathname
        }
      }).catch(() => {});
    };

    // Global change tracker for selects and checkboxes
    const handleChange = (e: Event) => {
      const target = e.target as HTMLInputElement | HTMLSelectElement;
      
      if (target.tagName === 'SELECT') {
        const selectName = target.name || (target as HTMLSelectElement).id || 'Unbekannte Auswahl';
        const selectedValue = (target as HTMLSelectElement).options[(target as HTMLSelectElement).selectedIndex]?.text || (target as HTMLSelectElement).value;
        
        logActivity({
          action_type: 'select.change',
          action_category: 'user_clicks',
          description: `Auswahl geändert: ${selectName} → ${selectedValue}`,
          entity_type: 'select',
          metadata: {
            select_name: selectName,
            selected_value: selectedValue,
            page: location.pathname
          }
        }).catch(() => {});
      }
      
      if (target.type === 'checkbox' || target.type === 'radio') {
        const inputName = target.name || target.id || 'Unbekannte Option';
        const checked = (target as HTMLInputElement).checked;
        
        logActivity({
          action_type: target.type === 'checkbox' ? 'checkbox.toggle' : 'radio.select',
          action_category: 'user_clicks',
          description: `${target.type === 'checkbox' ? 'Checkbox' : 'Radio'} ${checked ? 'aktiviert' : 'deaktiviert'}: ${inputName}`,
          entity_type: target.type,
          metadata: {
            input_name: inputName,
            checked: checked,
            page: location.pathname
          }
        }).catch(() => {});
      }
    };

    // Attach listeners
    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);
    document.addEventListener('change', handleChange, true);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
      document.removeEventListener('change', handleChange, true);
    };
  }, [location.pathname]);

  return null; // This component doesn't render anything
}

/**
 * Helper to get a human-readable page name from path
 */
function getPageName(path: string): string {
  const pathMap: Record<string, string> = {
    '/': 'Dashboard',
    '/login': 'Login',
    '/profile': 'Profil',
    '/admin': 'Administration',
    '/users': 'Benutzerverwaltung',
    '/roles': 'Rollenverwaltung',
    '/messages-admin': 'Nachrichten-Verwaltung',
    '/dienstplan': 'Dienstplan',
    '/storage': 'Lager',
    '/reports': 'Berichte',
    '/fahrzeugverwaltung': 'Fahrzeugverwaltung',
    '/meldewesen': 'Meldewesen',
    '/activity-log': 'Aktivitätsprotokoll',
    '/statistik': 'Statistik'
  };

  return pathMap[path] || path;
}

/**
 * Send activity log to backend
 */
async function logActivity(entry: {
  action_type: string;
  action_category: string;
  description: string;
  entity_type?: string;
  entity_id?: number;
  metadata?: Record<string, any>;
}): Promise<void> {
  try {
    // Verify token exists before attempting to log
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Support_OrgaPilot darf in keiner Organisation geloggt werden
    const username = localStorage.getItem('username');
    if (username === 'Support_OrgaPilot') {
      return; // Keine Logs für Support_OrgaPilot erstellen
    }
    
    await apiFetch('/messages/activity-log', {
      method: 'POST',
      body: JSON.stringify(entry)
    });
  } catch (error: any) {
    // Silent fail for auth errors - don't disrupt user experience
    if (error?.statusCode === 401 || error?.status === 401) {
      return;
    }
    console.debug('Activity logging failed:', error);
  }
}
